import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Clock, MapPin, Play, StopCircle, LogOut, Calendar, Key, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CleanerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [finishNotes, setFinishNotes] = useState('');
  const [showAccessNotes, setShowAccessNotes] = useState({});

  useEffect(() => {
    fetchVisits();
  }, [period]);

  const fetchVisits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cleaner/my-visits?period=${period}`);
      setVisits(response.data);
    } catch (error) {
      toast.error('Failed to fetch visits');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async (visitId) => {
    try {
      await axios.post(`${API_URL}/api/cleaner/visits/${visitId}/start`);
      toast.success('Visit started');
      fetchVisits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start visit');
    }
  };

  const openFinishDialog = (visit) => {
    setSelectedVisit(visit);
    setFinishDialogOpen(true);
  };

  const handleFinish = async () => {
    try {
      await axios.post(`${API_URL}/api/cleaner/visits/${selectedVisit.id}/finish`, {
        notes: finishNotes || ''
      });
      toast.success('Visit completed');
      setFinishDialogOpen(false);
      setSelectedVisit(null);
      setFinishNotes('');
      fetchVisits();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to finish visit';
      console.error('Finish visit error:', error.response?.data || error);
      toast.error(errorMsg);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleAccessNotes = (visitId) => {
    setShowAccessNotes(prev => ({ ...prev, [visitId]: !prev[visitId] }));
  };

  const getVisitLocation = async (locationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/${locationId}`);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cleaner-dashboard">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">My Schedule</h1>
              <p className="text-sm text-slate-600">{user?.full_name}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setPeriod('today')}
            className={period === 'today' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}
            data-testid="today-button"
          >
            Today
          </Button>
          <Button
            onClick={() => setPeriod('week')}
            className={period === 'week' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}
            data-testid="week-button"
          >
            This Week
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : visits.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No visits scheduled</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visits.map((visit) => (
              <div
                key={visit.id}
                className="bg-white border border-slate-200 p-6 shadow-sm"
                data-testid={`visit-card-${visit.id}`}
              >
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-slate-900 mb-1">{visit.client_name}</h3>
                    <div className="flex items-center gap-2 text-slate-600 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">
                        {visit.location_address}, {visit.location_city} {visit.location_postcode}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {format(new Date(visit.scheduled_start), 'EEE, MMM dd • HH:mm')}
                      </span>
                      <span className="text-slate-400">•</span>
                      <span className="text-sm">{visit.estimated_duration_minutes}min</span>
                    </div>
                  </div>

                  {visit.notes && (
                    <div className="bg-slate-50 border border-slate-200 p-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Notes</p>
                      <p className="text-sm text-slate-700">{visit.notes}</p>
                    </div>
                  )}

                  <div>
                    <Button
                      onClick={() => toggleAccessNotes(visit.id)}
                      variant="outline"
                      size="sm"
                      className="w-full mb-2"
                      data-testid={`toggle-access-${visit.id}`}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      {showAccessNotes[visit.id] ? 'Hide' : 'Show'} Access Information
                    </Button>

                    {showAccessNotes[visit.id] && (
                      <div className="bg-amber-50 border border-amber-200 p-4 space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                          <p className="text-xs text-amber-700 font-medium">Sensitive information - handle with care</p>
                        </div>
                        <div className="text-xs text-slate-600 space-y-1">
                          <p><span className="font-semibold">Full Address:</span> {visit.location_address}</p>
                          <p className="text-slate-500 italic">Access details available in location records</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 space-y-2">
                    {visit.status === 'SCHEDULED' && (
                      <Button
                        onClick={() => handleStart(visit.id)}
                        className="w-full py-4 text-xl font-bold uppercase tracking-widest shadow-md active:scale-95 transition-transform bg-orange-500 hover:bg-orange-600"
                        data-testid={`start-visit-${visit.id}`}
                      >
                        <Play className="w-6 h-6 mr-2" />
                        Start Job
                      </Button>
                    )}

                    {visit.status === 'IN_PROGRESS' && (
                      <>
                        <div className="bg-amber-50 border border-amber-200 p-3 text-center">
                          <p className="text-sm font-bold text-amber-700">JOB IN PROGRESS</p>
                        </div>
                        <Button
                          onClick={() => openFinishDialog(visit)}
                          className="w-full py-4 text-xl font-bold uppercase tracking-widest shadow-md active:scale-95 transition-transform bg-emerald-500 hover:bg-emerald-600"
                          data-testid={`finish-visit-${visit.id}`}
                        >
                          <StopCircle className="w-6 h-6 mr-2" />
                          Finish Job
                        </Button>
                      </>
                    )}

                    {visit.status === 'DONE' && (
                      <div className="bg-emerald-50 border border-emerald-200 p-3 text-center">
                        <p className="text-sm font-bold text-emerald-700">COMPLETED</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={finishDialogOpen} onOpenChange={setFinishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Visit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="finish_notes">Notes (Optional)</Label>
              <Textarea
                id="finish_notes"
                value={finishNotes}
                onChange={(e) => setFinishNotes(e.target.value)}
                rows={4}
                placeholder="Add any notes about this visit..."
                data-testid="finish-notes-input"
              />
            </div>
            <Button
              onClick={handleFinish}
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              data-testid="confirm-finish-button"
            >
              Confirm Completion
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
